(() => {
  'use strict';

  const ccRuntimeKey = 'CCT10RevealRuntime';
  const ccRevealSelector = '.cc-reveal';
  const ccReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (window[ccRuntimeKey]) {
    window[ccRuntimeKey].scan(document);
    return;
  }

  let ccRevealObserver = null;
  const ccObservedElements = new WeakSet();
  const ccGatedElements = new Set();
  const ccEligibleGatedElements = new Set();
  const ccInitialScrollY = window.scrollY;
  let ccScrollGateUnlocked = window.location.hash !== '' || ccInitialScrollY > 0;
  let ccScrollGateListening = false;
  let ccScrollGateHandler = null;

  const ccCollectRevealElements = (root) => {
    const elements = [];

    if (root instanceof Element && root.matches(ccRevealSelector)) {
      elements.push(root);
    }

    if (root instanceof Document || root instanceof Element || root instanceof DocumentFragment) {
      elements.push(...root.querySelectorAll(ccRevealSelector));
    }

    return elements;
  };

  const ccIsPrearmedElement = (element) => element.dataset.ccRevealPrearm === 'true';
  const ccIsAfterScrollElement = (element) => (
    ccIsPrearmedElement(element) && element.dataset.ccRevealAfterScroll === 'true'
  );

  const ccDetachScrollGateListener = () => {
    if (!ccScrollGateListening || !ccScrollGateHandler) {
      return;
    }

    window.removeEventListener('scroll', ccScrollGateHandler);
    ccScrollGateListening = false;
  };

  const ccCleanupGatedElement = (element) => {
    ccGatedElements.delete(element);
    ccEligibleGatedElements.delete(element);

    if (ccGatedElements.size === 0) {
      ccDetachScrollGateListener();
    }
  };

  const ccRevealImmediately = (root) => {
    ccCollectRevealElements(root).forEach((element) => {
      ccRevealObserver?.unobserve(element);
      ccObservedElements.delete(element);
      ccCleanupGatedElement(element);
      element.classList.remove('cc-reveal-pending');
      element.classList.add('cc-is-revealed');
    });
  };

  const ccRevealElement = (element) => {
    element.classList.add('cc-is-revealed');
    ccRevealObserver?.unobserve(element);
    ccObservedElements.delete(element);
    ccCleanupGatedElement(element);
  };

  const ccStartReveal = (element) => {
    if (ccReducedMotion.matches) {
      ccRevealImmediately(element);
      return;
    }

    if (ccIsPrearmedElement(element) && element.classList.contains('cc-reveal-pending')) {
      ccRevealElement(element);
      return;
    }

    try {
      element.classList.add('cc-reveal-pending');
      element.getBoundingClientRect();
    } finally {
      ccRevealElement(element);
    }
  };

  const ccFailVisibleGatedElements = () => {
    [...ccGatedElements].forEach((element) => ccRevealImmediately(element));
    ccEligibleGatedElements.clear();
  };

  ccScrollGateHandler = () => {
    try {
      if (window.scrollY <= ccInitialScrollY) {
        return;
      }

      ccScrollGateUnlocked = true;
      ccDetachScrollGateListener();

      [...ccEligibleGatedElements].forEach((element) => {
        if (!element.isConnected) {
          ccCleanupGatedElement(element);
          return;
        }

        if (!element.classList.contains('cc-is-revealed')) {
          ccStartReveal(element);
        }
      });
    } catch {
      ccScrollGateUnlocked = true;
      ccDetachScrollGateListener();
      ccFailVisibleGatedElements();
    }
  };

  const ccEnsureScrollGateListener = () => {
    if (ccScrollGateUnlocked || ccScrollGateListening || ccGatedElements.size === 0) {
      return;
    }

    try {
      window.addEventListener('scroll', ccScrollGateHandler, { passive: true });
      ccScrollGateListening = true;
    } catch {
      ccScrollGateUnlocked = true;
      ccFailVisibleGatedElements();
    }
  };

  const ccHandlePrearmedEntry = (entry) => {
    const element = entry.target;
    const isEligible = entry.isIntersecting || entry.boundingClientRect.top < 0;

    if (element.classList.contains('cc-is-revealed')) {
      return;
    }

    if (ccIsAfterScrollElement(element) && !ccScrollGateUnlocked) {
      if (isEligible) {
        ccEligibleGatedElements.add(element);
      } else {
        ccEligibleGatedElements.delete(element);
      }

      return;
    }

    if (isEligible) {
      ccStartReveal(element);
    }
  };

  const ccCreateObserver = () => {
    if (ccRevealObserver || ccReducedMotion.matches || !('IntersectionObserver' in window)) {
      return ccRevealObserver;
    }

    try {
      ccRevealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (ccIsPrearmedElement(entry.target)) {
              try {
                ccHandlePrearmedEntry(entry);
              } catch {
                ccRevealImmediately(entry.target);
              }

              return;
            }

            if (
              !entry.target.classList.contains('cc-is-revealed') &&
              (entry.isIntersecting || entry.boundingClientRect.top < 0)
            ) {
              ccStartReveal(entry.target);
            }
          });
        },
        {
          threshold: 0.08,
          rootMargin: '0px 0px -10% 0px',
        }
      );
    } catch {
      ccRevealObserver = null;
    }

    return ccRevealObserver;
  };

  const ccScan = (root = document) => {
    if (ccReducedMotion.matches) {
      ccRevealImmediately(root);
      return;
    }

    const observer = ccCreateObserver();

    if (!observer) {
      ccRevealImmediately(root);
      return;
    }

    ccCollectRevealElements(root).forEach((element) => {
      if (
        !element.isConnected ||
        ccObservedElements.has(element) ||
        element.classList.contains('cc-is-revealed')
      ) {
        return;
      }

      try {
        if (ccIsPrearmedElement(element)) {
          element.classList.add('cc-reveal-pending');
          element.getBoundingClientRect();

          if (ccIsAfterScrollElement(element) && !ccScrollGateUnlocked) {
            ccGatedElements.add(element);
            ccEnsureScrollGateListener();

            if (element.classList.contains('cc-is-revealed')) {
              return;
            }
          }
        }

        observer.observe(element);
        ccObservedElements.add(element);
      } catch {
        ccRevealImmediately(element);
      }
    });
  };

  const ccHandleReducedMotionChange = () => {
    if (ccReducedMotion.matches) {
      ccRevealObserver?.disconnect();
      ccRevealObserver = null;
      ccRevealImmediately(document);
      return;
    }

    ccScan(document);
  };

  const ccHandleSectionUnload = (event) => {
    ccCollectRevealElements(event.target).forEach((element) => {
      if (!ccIsPrearmedElement(element)) {
        return;
      }

      ccRevealObserver?.unobserve(element);
      ccObservedElements.delete(element);
      ccCleanupGatedElement(element);
    });
  };

  window[ccRuntimeKey] = { scan: ccScan };
  document.addEventListener('shopify:section:load', (event) => ccScan(event.target));
  document.addEventListener('shopify:section:unload', ccHandleSectionUnload);
  ccReducedMotion.addEventListener?.('change', ccHandleReducedMotionChange);
  ccScan(document);
})();
